import { useRef, useState, type ChangeEvent, type ReactNode } from 'react';
import { LuCamera, LuTrash2 } from 'react-icons/lu';
import { HelperText } from '../Field/HelperText';
import { FieldError } from '../Field/FieldError';
import { Spinner } from '../Spinner/Spinner';
import * as s from './ImageUploadPlate.css';

interface ImageUploadPlateProps {
  /** Object URL of the current image, or null to show the fallback. */
  imageUrl?: string | null;
  /** Rendered (centered) when there is no image — typically initials. */
  fallback: ReactNode;
  /** Plate shape: a circular avatar or a square, framed logo plate. */
  shape: 'round' | 'square';
  /** Accepted MIME types, e.g. ['image/png', 'image/jpeg']. */
  accept: string[];
  /** Maximum file size in bytes; larger selections are rejected. */
  maxBytes: number;
  /** Helper line under the plate (format/size guidance). */
  helpText: string;
  /** Accessible label for the change (camera) action. */
  changeLabel: string;
  /** Accessible label for the remove (trash) action. */
  removeLabel: string;
  /** Message shown when the selected file's type is not in `accept`. */
  typeError: string;
  /** Message shown when the selected file exceeds `maxBytes`. */
  sizeError: string;
  /** Whether an upload/remove is in flight — shows a spinner, hides actions. */
  busy?: boolean;
  /** Whether the remove action is offered (an image exists to remove). */
  canRemove?: boolean;
  /** Called with a validated file when the user picks one. */
  onSelect: (file: File) => void;
  /** Called when the user triggers removal. */
  onRemove: () => void;
}

/**
 * Avatar/logo upload plate with a hover overlay carrying change + remove icon
 * actions. Owns the hidden file input, type/size validation, and inline error
 * state so callers only handle the (already-validated) upload and removal.
 * Shared by the profile photo (round) and the fund logo (square).
 */
export function ImageUploadPlate({
  imageUrl,
  fallback,
  shape,
  accept,
  maxBytes,
  helpText,
  changeLabel,
  removeLabel,
  typeError,
  sizeError,
  busy = false,
  canRemove = false,
  onSelect,
  onRemove,
}: ImageUploadPlateProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!accept.includes(file.type)) {
      setError(typeError);
      return;
    }
    if (file.size > maxBytes) {
      setError(sizeError);
      return;
    }
    setError(null);
    onSelect(file);
  };

  const showImage = Boolean(imageUrl);

  return (
    <div className={s.row}>
      <div
        className={`${s.plate} ${shape === 'round' ? s.round : s.square}`}
        data-busy={busy}
      >
        {showImage ? (
          <img src={imageUrl ?? undefined} alt="" className={s.image} />
        ) : (
          fallback
        )}
        <div className={s.overlay}>
          {busy ? (
            <Spinner size="sm" />
          ) : (
            <>
              <button
                type="button"
                className={s.action}
                onClick={() => inputRef.current?.click()}
                aria-label={changeLabel}
              >
                <LuCamera size={18} aria-hidden />
              </button>
              {canRemove && (
                <button
                  type="button"
                  className={s.action}
                  onClick={onRemove}
                  aria-label={removeLabel}
                >
                  <LuTrash2 size={18} aria-hidden />
                </button>
              )}
            </>
          )}
        </div>
      </div>
      <div className={s.actions}>
        <HelperText>{helpText}</HelperText>
        {error && <FieldError>{error}</FieldError>}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept.join(',')}
        hidden
        onChange={handleChange}
      />
    </div>
  );
}
